import { useAuth0 } from '@/auth/index' // Asumiendo que esta ruta es correcta
import { useState, useCallback, useEffect, useRef } from 'react'
import useConversationsStore from '@/stores/useConversationsStore'
import { mapConversationsFromApiResponse } from '../services/conversations-mapper'
import {
  Conversation as ClientConversation,
  ServerConversation,
} from '@/routes/plus/models/Conversation.model'
import { useAwsCredentialsManager } from '../context/AWS-credentials.context'
import { getConfig } from '@/config/runtime'

type ConversationsApiResponse = {
  conversations: ServerConversation[]
  nextToken: string | null
  pinnedConversations?: ServerConversation[] // Optional for backward compatibility
}


interface UseConversationsFetcherReturn {
  isLoading: boolean
  error: Error | null
  fetchConversationsList: () => Promise<void>
}

export const useConversationsFetcher = (): UseConversationsFetcherReturn => {
  const setConversationsInStore = useConversationsStore((state) => state.setConversations)
  const conversationsFromStore = useConversationsStore((state) => state.conversations)
  const lastApplicationId = useConversationsStore((state) => state.lastApplicationId)

  const abortControllerRef = useRef<AbortController | null>(null)
  const lastProcessedApplicationIdRef = useRef<string | null>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [initialFetchAttempted, setInitialFetchAttempted] = useState<boolean>(false)

  const { user } = useAuth0()

  const {
    applicationId,
    getValidCredentials,
    isLoading: credentialsLoading,
    isAuthorized,
  } = useAwsCredentialsManager()

  const fetchConversationsList = useCallback(async () => {
    if (!isAuthorized) {
      console.warn('fetchConversationsList: User is not authorized. Skipping fetch.')
      return
    }

    if (isLoading) {
      console.warn('fetchConversationsList: Fetch already in progress.')
      return
    }

    if (credentialsLoading) {
      console.warn('fetchConversationsList: AWS credentials are still loading. Fetch deferred.')
      return
    }

    console.log('USER', user)
    const fetchedCredentials = await getValidCredentials()

    if (
      !fetchedCredentials?.AccessKeyId ||
      !fetchedCredentials.SecretAccessKey ||
      !fetchedCredentials.SessionToken ||
      !applicationId
    ) {
      const credsError = new Error(
        'AWS credentials or Application ID are not available or incomplete. Cannot fetch conversations.'
      )
      console.error('fetchConversationsList:', credsError.message)
      setError(credsError)
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(`${getConfig().plus.apiUrl}/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessKeyId: fetchedCredentials.AccessKeyId,
          secretAccessKey: fetchedCredentials.SecretAccessKey,
          sessionToken: fetchedCredentials.SessionToken,
          applicationId: applicationId,
          email: user.email,
        }),
        signal: abortController.signal,
      })

      if (!response.ok) {
        let errorBody = `API request failed with status ${response.status}`
        try {
          const errData = await response.json()
          errorBody = errData.message || JSON.stringify(errData) || errorBody
        } catch (e) {
          console.error('fetchConversationsList: Error parsing error response:', e)
        }
        throw new Error(errorBody)
      }

      const data = (await response.json()) as ConversationsApiResponse

      if (!data || !Array.isArray(data.conversations)) {
        console.error(
          'fetchConversationsList: Invalid data structure received from conversations API:',
          data
        )
        throw new Error('Invalid data structure received from conversations API.')
      }

      const mappedConversations: ClientConversation[] = mapConversationsFromApiResponse(
        data.conversations,
        data.pinnedConversations
      )

      setConversationsInStore(mappedConversations, applicationId, data.nextToken)
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        console.log('fetchConversationsList: Request aborted due to tenant switch')
        return
      }

      const fetchError = e instanceof Error ? e : new Error(String(e))
      console.error('fetchConversationsList: Error during fetch:', fetchError)
      setError(fetchError)
    } finally {
      setIsLoading(false)
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
      }
    }
  }, [
    isAuthorized,
    isLoading,
    credentialsLoading,
    getValidCredentials,
    applicationId,
    setConversationsInStore,
  ])

  useEffect(() => {
    if (!isAuthorized || !applicationId || credentialsLoading) return

    if (lastProcessedApplicationIdRef.current === applicationId) return

    const applicationIdChanged = lastApplicationId !== null && lastApplicationId !== applicationId

    if (applicationIdChanged) {
      console.log('🔄 Application ID changed, clearing conversations and re-fetching...', {
        from: lastApplicationId,
        to: applicationId,
      })

      if (abortControllerRef.current) {
        console.log('🚫 Aborting ongoing fetch for previous application ID')
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }

      setConversationsInStore([], applicationId, null)

      setInitialFetchAttempted(false)
      setError(null)
      setIsLoading(false)

      lastProcessedApplicationIdRef.current = applicationId

      fetchConversationsList()
    } else if (conversationsFromStore.length === 0 && !initialFetchAttempted) {
      console.log('🚀 Initial fetch of conversations for applicationId:', applicationId)

      lastProcessedApplicationIdRef.current = applicationId

      setInitialFetchAttempted(true)
      fetchConversationsList()
    }
  }, [
    isAuthorized,
    applicationId,
    lastApplicationId,
    credentialsLoading,
    conversationsFromStore.length,
    initialFetchAttempted,
  ])

  return { isLoading, error, fetchConversationsList }
}
